import { useState } from 'react';
import { formatPercent, formatRial } from '@kia/shared';
import { useApiResource } from '../lib/useApiResource';
import { Button, Card, ErrorRetry, Field, JalaliDateInput, Kpi, Money } from '../ui';
import { AbsorptionSkewCard } from './AbsorptionSkewCard';

interface Line {
  revenue: number;
  cost: number;
  gross: number;
  margin: number | null;
  share: number;
  qty: number;
}
interface Profit {
  window: { from: string; to: string };
  totals: {
    revenue: number;
    cost: number;
    gross: number;
    margin: number | null;
    deliveries: number;
  };
  byCustomer: ({ customerId: number | null; customer: string } & Line)[];
  byFamily: ({ prefix: string; label: string } & Line)[];
  byProduct: ({ code: string; name: string } & Line)[];
  byType: ({ type: string } & Line)[];
  byMonth: { ym: string; revenue: number; cost: number; gross: number }[];
}

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);
const QUICK: [label: string, from: () => string, to: () => string][] = [
  ['امسال (YTD)', () => `${new Date().getFullYear()}-01-01`, today],
  ['۳۰ روز اخیر', () => daysAgo(30), today],
  ['۹۰ روز اخیر', () => daysAgo(90), today],
  ['همهٔ زمان‌ها', () => '2000-01-01', today],
];

function ProfitTable<T extends Line>({
  title,
  rows,
  nameOf,
  onRowClick,
}: {
  title: string;
  rows: T[];
  nameOf: (r: T) => { main: string; sub?: string };
  /** When set, rows are clickable (drill-down); a ↩ affordance is shown in the header. */
  onRowClick?: (r: T) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <Card title={title}>
      <table className="table">
        <thead>
          <tr>
            <th>نام</th>
            <th>تعداد</th>
            <th>درآمد</th>
            <th>سود ناخالص</th>
            <th>حاشیه</th>
            <th>سهم از فروش</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const n = nameOf(r);
            return (
              <tr
                key={i}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
                title={onRowClick ? 'برای تمرکز روی این مشتری کلیک کنید' : undefined}
              >
                <td>
                  {onRowClick ? '🔍 ' : ''}
                  {n.main}
                  {n.sub ? <div className="muted num">{n.sub}</div> : null}
                </td>
                <td className="num">{r.qty}</td>
                <td>{formatRial(r.revenue)}</td>
                <td style={{ color: r.gross >= 0 ? 'var(--green)' : 'var(--red)' }}>
                  {formatRial(r.gross)}
                </td>
                <td>{formatPercent(r.margin)}</td>
                <td className="num">{formatPercent(r.share)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

export function BiScreen() {
  const [from, setFrom] = useState(QUICK[0]![1]());
  const [to, setTo] = useState(today());
  const [focus, setFocus] = useState<{ id: number; name: string } | null>(null);

  // The primary read now flows through useApiResource, keyed by the current window +
  // focused customer. Both the initial mount read and every param change (quick-picks /
  // apply / drill-down) re-fetch through the same resource, so a failure on ANY of them
  // lands in `status: 'error'` with a retry instead of a stuck-blank / partial screen.
  const cq = focus?.id != null ? `&customerId=${focus.id}` : '';
  const { data, status, error, reload } = useApiResource<Profit>(
    `/api/analytics/profit?from=${from}&to=${to}${cq}`,
    [from, to, focus?.id],
  );

  function pick(f: string, t: string) {
    setFrom(f);
    setTo(t);
  }
  function drillTo(row: { customerId: number | null; customer: string }) {
    if (row.customerId == null) return; // the "no customer" (—) bucket isn't a drill target
    setFocus({ id: row.customerId, name: row.customer });
  }
  function clearFocus() {
    setFocus(null);
  }

  return (
    <>
      <Card title="هوش تجاری — سودآوری واقعی (بر پایهٔ خروج انبار)">
        <p className="muted" style={{ marginTop: 0 }}>
          درآمد و بهای «واقعی» از تحویل‌های انجام‌شده (نه برآورد پیشنهادهای باز). بازهٔ دلخواه را
          انتخاب کنید.
        </p>
        <div className="row">
          {QUICK.map(([label, f, t]) => (
            <Button key={label} variant="secondary" onClick={() => pick(f(), t())}>
              {label}
            </Button>
          ))}
          <Field label="از تاریخ">
            <JalaliDateInput value={from} onValue={setFrom} />
          </Field>
          <Field label="تا تاریخ">
            <JalaliDateInput value={to} onValue={setTo} />
          </Field>
          <Button variant="ghost" onClick={() => reload()}>
            اعمال بازه
          </Button>
        </div>
        {focus ? (
          <div
            className="banner"
            style={{
              borderRight: '4px solid var(--brand)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <b style={{ flex: 1 }}>
              تمرکز روی مشتری: «{focus.name}» — جدول‌های زیر فقط این مشتری‌اند.
            </b>
            <Button variant="secondary" onClick={clearFocus}>
              ↩ حذف تمرکز (همهٔ مشتریان)
            </Button>
          </div>
        ) : null}
        {status === 'error' ? (
          <ErrorRetry error={error} onRetry={() => reload()} />
        ) : status === 'loading' && !data ? (
          <p className="muted">در حال بارگذاری…</p>
        ) : null}
        {data ? (
          <div style={{ marginTop: 8 }}>
            <Kpi label="درآمد واقعی" value={<Money value={data.totals.revenue} />} />
            <Kpi label="بهای واقعی" value={<Money value={data.totals.cost} />} />
            <Kpi
              label="سود ناخالص واقعی"
              value={<Money value={data.totals.gross} />}
              tone={data.totals.gross >= 0 ? 'good' : 'bad'}
            />
            <Kpi label="حاشیه" value={formatPercent(data.totals.margin)} />
            <Kpi label="تعداد تحویل" value={data.totals.deliveries} />
          </div>
        ) : null}
        {data && data.totals.deliveries === 0 ? (
          <div className="banner">در این بازه خروج انباری ثبت نشده است.</div>
        ) : null}
      </Card>

      {data ? (
        <>
          {focus ? null : (
            <ProfitTable
              title={`سود به تفکیک مشتری (${data.byCustomer.length}) — برای تمرکز کلیک کنید`}
              rows={data.byCustomer}
              nameOf={(r) => ({ main: r.customer })}
              onRowClick={(r) => drillTo(r)}
            />
          )}
          <ProfitTable
            title={`${focus ? `«${focus.name}» — ` : ''}سود به تفکیک خانوادهٔ محصول (${data.byFamily.length})`}
            rows={data.byFamily}
            nameOf={(r) => ({ main: r.label || r.prefix, sub: r.prefix })}
          />
          {data.byType.length > 0 ? (
            <ProfitTable
              title={`${focus ? `«${focus.name}» — ` : ''}سود به تفکیک نوع محصول (${data.byType.length})`}
              rows={data.byType}
              nameOf={(r) => ({ main: r.type })}
            />
          ) : null}
          <ProfitTable
            title={`${focus ? `«${focus.name}» — ` : ''}سود به تفکیک محصول (۲۰ قلم برتر)`}
            rows={data.byProduct}
            nameOf={(r) => ({ main: r.name, sub: r.code })}
          />
          {data.byMonth.length > 1 ? (
            <Card title={`${focus ? `«${focus.name}» — ` : ''}روند ماهانه`}>
              <table className="table">
                <thead>
                  <tr>
                    <th>ماه</th>
                    <th>درآمد واقعی</th>
                    <th>بهای تمام‌شده</th>
                    <th>سود ناخالص</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byMonth.map((m) => (
                    <tr key={m.ym}>
                      <td className="num">{m.ym}</td>
                      <td>{formatRial(m.revenue)}</td>
                      <td>{formatRial(m.cost)}</td>
                      <td>{formatRial(m.gross)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}
        </>
      ) : null}

      {/* Absorption-skew diagnostic (accuracy #4) — read-only; surfaces the winding-weight distribution skew. */}
      <AbsorptionSkewCard />
    </>
  );
}
